import type { Operation, RequestBody, ResponseBody } from '@platform/openapi';
import type { Product } from '../catalog/product.ts';
import { MAX_COPIES_PER_VARIANT, type LabelSelection } from '../labels/labels.ts';

export type { Product };

export type MovementList = ResponseBody<Operation<'/admin/stock-movements', 'get'>>;
export type Movement = MovementList['items'][number];
export type MovementKind = Movement['kind'];
export type MovementState = Movement['state'];
export type RecordMovementBody = RequestBody<Operation<'/admin/stock-movements', 'post'>>;

export type LocationList = ResponseBody<Operation<'/admin/locations', 'get'>>;
export type Location = LocationList['items'][number];

export type StockLevelList = ResponseBody<Operation<'/admin/inventory/low-stock', 'get'>>;
export type StockLevel = StockLevelList['items'][number];

export const INVENTORY_WRITE_ROLE = 'inventory:write';

export const INVENTORY_TABS = ['levels', 'movements', 'depots'] as const;
export type InventoryTab = (typeof INVENTORY_TABS)[number];

export const MAX_MOVEMENT_NOTE_LENGTH = 2000;

export const defaultDepotOf = (locations: readonly Location[]): string =>
  (locations.find((location) => location.isDefault) ?? locations[0])?.id ?? '';

export const onHandAt = (levels: readonly StockLevel[], variantId: string, locationId: string): number =>
  levels.find((level) => level.variantId === variantId && level.locationId === locationId)?.onHand ?? 0;

export type StockHealth = 'ok' | 'low' | 'out';

export const healthOfLevel = (level: StockLevel | undefined): StockHealth => {
  if (level === undefined || level.available <= 0) return 'out';
  if (level.lowStockThreshold !== null && level.available <= level.lowStockThreshold) return 'low';
  return 'ok';
};

export const sumAvailable = (levels: readonly StockLevel[]): number =>
  levels.reduce((total, level) => total + level.available, 0);

export const healthOfLevels = (levels: readonly StockLevel[]): StockHealth => {
  if (sumAvailable(levels) <= 0) return 'out';
  return levels.some((level) => healthOfLevel(level) === 'low') ? 'low' : 'ok';
};

export const MOVEMENT_KINDS = [
  'restock',
  'restock_pending',
  'commit',
  'write_off',
  'adjustment',
  'import',
] as const satisfies readonly MovementKind[];

export const MOVEMENT_STATES = ['applied', 'pending', 'voided'] as const satisfies readonly MovementState[];

export type VariantLabel = { readonly sku: string; readonly productName: string; readonly productId: string };

const SHORT_ID_LENGTH = 8;

export const shortId = (id: string): string => `…${id.slice(-SHORT_ID_LENGTH)}`;

const LABELLED_KINDS: ReadonlySet<Movement['kind']> = new Set(['restock', 'restock_pending', 'import']);

export const labelSelectionOf = (
  labels: ReadonlyMap<string, VariantLabel>,
  movement: Movement,
): LabelSelection | null => {
  const label = labels.get(movement.variantId);
  if (label === undefined || movement.quantity <= 0 || !LABELLED_KINDS.has(movement.kind)) return null;
  return {
    productIds: [label.productId],
    focus: { variantId: movement.variantId, quantity: Math.min(movement.quantity, MAX_COPIES_PER_VARIANT) },
  };
};

export const locationNames = (locations: readonly Location[]): ReadonlyMap<string, string> =>
  new Map(locations.map((location) => [location.id, location.name]));
